import { useEffect, useId, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button, cx } from "./ui";

/** Modal built on native <dialog>: focus trap, Esc and backdrop handling come from the browser. */
export function Dialog({
  open,
  onClose,
  title,
  children,
  footer,
  width = 520,
  dismissable = true,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
  /** When false, Esc and backdrop clicks don't close it (e.g. a password shown once). */
  dismissable?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const { t } = useTranslation();

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      // showModal focuses the first focusable thing — the close button. Start in the first field instead.
      d.querySelector<HTMLElement>("input:not([type=hidden]):not([type=checkbox]):not(:disabled), textarea:not(:disabled), select:not(:disabled)")?.focus();
    }
    if (!open && d.open) d.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onCancel={(e) => {
        // React bubbles `cancel` from a nested dialog: Esc there must not close this one too.
        if (e.target !== ref.current) return;
        e.preventDefault();
        if (dismissable) onClose();
      }}
      onClick={(e) => {
        if (dismissable && e.target === ref.current) onClose();
      }}
      style={{ maxWidth: `min(${width}px, calc(100vw - 32px))` }}
      className="m-auto w-full rounded-xl border border-border bg-surface p-0 text-text shadow-[var(--shadow)]"
    >
      {open && (
        <div className="flex max-h-[calc(100vh-64px)] flex-col">
          <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-4">
            <h2 id={titleId} className="m-0 text-[17px] font-semibold">
              {title}
            </h2>
            {dismissable && (
              <Button variant="ghost" size="sm" icon="x" aria-label={t("common.close")} onClick={onClose} />
            )}
          </div>
          <div className="flex flex-col gap-4 overflow-y-auto px-5 py-4">{children}</div>
          {footer && <div className="flex flex-wrap justify-end gap-2 border-t border-border px-5 py-3.5">{footer}</div>}
        </div>
      )}
    </dialog>
  );
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  body,
  confirmLabel,
  danger,
  loading,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: ReactNode;
  body: ReactNode;
  confirmLabel: ReactNode;
  danger?: boolean;
  loading?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      width={460}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant={danger ? "danger" : "primary"} loading={loading} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className={cx("m-0 text-text-2")}>{body}</p>
    </Dialog>
  );
}
